import firebase from "firebase/compat/app";
import { useEffect, useState } from "react";
import "firebase/compat/auth";
import "firebase/compat/firestore";
import "firebase/compat/storage";
import moment from "moment";
import { consola } from "consola";
import { useRecoilValue, atom, useSetRecoilState } from "recoil";
import userDataState from "../atoms/userDataAtom";
import organizationIdState from "../atoms/organizationIdAtom";

export const ReportStates = {
  processed: "processed",
  unprocessed: "unprocessed",
  any: "any",
};

export const CurrentReportState = atom({
  key: "CurrentReportState",
  default: ReportStates.unprocessed,
});

export const CurrentReportFilter = atom({
  key: "CurrentReportFilter",
  default: {
    state: ReportStates.unprocessed,
    start: moment().subtract(14, "days").startOf("day"),
    end: moment().endOf("day"),
    limit: 50,
  },
});

export const Reports = atom({
  key: "Reports",
  default: [],
});

export const ReportsLoading = atom({
  key: "ReportsLoading",
  default: true,
});

const useReportHistory = () => {
  const setReports = useSetRecoilState(Reports);
  const setReportsLoading = useSetRecoilState(ReportsLoading);
  const currentReportFilter = useRecoilValue(CurrentReportFilter);

  const coach = useRecoilValue(userDataState);

  const organizationId = useRecoilValue(organizationIdState);

  useEffect(() => {
    if (!coach || !organizationId) {
      setReports([]);
      setReportsLoading(false);
      return;
    }
    setReportsLoading(true);
    // Clear stale rows so tab switches do not show the prior view's data.
    setReports([]);
    let reportRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("reports");
    
    // Admins see all reports, non-admins filter by coaches array
    if (coach.role !== "admin") {
      reportRef = reportRef.where("coaches", "array-contains", coach.uid);
    }
    
    reportRef = reportRef.where("status", "==", currentReportFilter.state);

    if (
      currentReportFilter.start &&
      currentReportFilter.state === ReportStates.processed
    ) {
      reportRef = reportRef.where(
        "createdOn",
        ">=",
        currentReportFilter.start.toDate(),
      );
    }
    if (
      currentReportFilter.end &&
      currentReportFilter.state === ReportStates.processed
    ) {
      reportRef = reportRef.where(
        "createdOn",
        "<=",
        currentReportFilter.end.toDate(),
      );
    }

    if (currentReportFilter.state === ReportStates.unprocessed) {
      reportRef = reportRef.orderBy("createdOn", "asc");
    } else {
      reportRef = reportRef.orderBy("createdOn", "desc");
    }

    if (
      currentReportFilter.limit &&
      currentReportFilter.state === ReportStates.processed
    ) {
      reportRef = reportRef.limit(currentReportFilter.limit);
    }

    const unsubscribe = reportRef.onSnapshot(
      (snapshot) => {
        consola.debug("++++++ SNAPSHOT -> ReportData: useReportHistory");
        setReports(
          snapshot.docs.map((d) => {
            return {
              id: d.id,
              ...d.data(),
              createdOn: d.data().createdOn.toDate(),
            };
          }),
        );
        setReportsLoading(false);
      },
      (error) => {
        setReportsLoading(false);
        if (error.code === "permission-denied") {
          consola.error(
            "Permission denied: Unable to access report data.",
            error,
          );
        } else if (error.code === "failed-precondition") {
          consola.error(
            "Firestore index missing. Please create the required composite index.",
            error,
          );
        } else {
          consola.error("++++++ ERROR -> ReportData: useReportHistory", error);
        }
      },
    );

    return () => {
      consola.debug("++++++ UNSUBSCRIBE -> ReportData: useReportHistory");
      unsubscribe();
    };
  }, [
    coach?.uid,
    organizationId,
    currentReportFilter.state,
    // Convert moment objects to timestamps for proper dependency tracking
    currentReportFilter.start?.valueOf(),
    currentReportFilter.end?.valueOf(),
    currentReportFilter.limit,
    setReports,
    setReportsLoading,
  ]);

  return null;
};

export const useReportHistoryByUID = (uid, organizationId, limit = 10) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    console.log("useReportHistoryByUID called with:", {
      uid,
      organizationId,
      limit,
    });
    
    if (!uid || !organizationId) {
      console.log(
        "useReportHistoryByUID: Missing uid or organizationId, setting empty reports",
      );
      setReports([]);
      return;
    }

    setLoading(true);
    setError(null);

    const reportRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("reports")
      .where("uid", "==", uid)
      .orderBy("createdOn", "desc")
      .limit(limit);

    console.log("useReportHistoryByUID: Starting query...");

    reportRef
      .get()
      .then((snapshot) => {
        console.log(
          "useReportHistoryByUID: Query successful, doc count:",
          snapshot.docs.length,
        );
        consola.debug("++++++ GET -> ReportData: useReportHistoryByUID");
        const reportsData = snapshot.docs.map((d) => {
          return {
            id: d.id,
            ...d.data(),
            createdOn: d.data().createdOn.toDate(),
          };
        });
        console.log(
          "useReportHistoryByUID: Processed reports:",
          reportsData.length,
          reportsData.map((r) => ({ id: r.id, createdOn: r.createdOn })),
        );
        setReports(reportsData);
        setLoading(false);
      })
      .catch((error) => {
        console.error("useReportHistoryByUID: Query failed:", error);
        setLoading(false);
        if (error.code === "permission-denied") {
          consola.error(
            "Permission denied: Unable to access report data.",
            error,
          );
          setError("Permission denied: Unable to access report data.");
        } else {
          consola.error(
            "++++++ ERROR -> ReportData: useReportHistoryByUID",
            error,
          );
          setError(error.message);
        }
      });
  }, [uid, organizationId, limit]);

  return { reports, loading, error };
};

export default useReportHistory;
